/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { Component } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { formatWithCommas } from '../utils/Format';
import { formatToNumber } from '../utils/Format';
import { VALIDATION_RULES } from '../config/validationRules';
import { UnifiedInputControl } from './InputComponents';
import { getInputMetadata } from '../utils/InputMetadata';
import { CollapsibleSection } from './CollapsibleSection';

export class Inputs extends Component {
    constructor(props) {
        super(props);
        this.toggleSection = this.toggleSection.bind(this);
        this.validateField = this.validateField.bind(this);
        this.state = {
            sections: {
                Personal: false,
                '401': true,
                ESOP: true
            },
            invalidFields: new Set(),
            editingField: null,
            currencyInputs: {
                currentComp: formatWithCommas(props.currentComp || 0),
                catchUpAmount: formatWithCommas(props.catchUpAmount || 0),
                current401: formatWithCommas(props.current401 || 0),
                openingCashBalance: formatWithCommas(props.openingCashBalance || 0)
            }
        };
    }

    handleFocus = (fieldKey) => {
        this.setState({ editingField: fieldKey });
    };

    handleBlur = (fieldKey) => {
        // finish edit -> pretty print and clear editing flag
        this.setState(s => ({
            currencyInputs: {
                ...s.currencyInputs,
                [fieldKey]: formatWithCommas(s.currencyInputs[fieldKey])
            },
            editingField: null
        }));
    };

    toggleSection = (sectionName) => {
        this.setState(prev => ({
            sections: {
                ...prev.sections,
                [sectionName]: !prev.sections[sectionName]
            }
        }));
    };

    setFieldValidity = (fieldName, isValid) => {
        this.setState(prev => {
            const invalidFields = new Set(prev.invalidFields);
            isValid ? invalidFields.delete(fieldName) : invalidFields.add(fieldName);
            return { invalidFields };
        });
    };

    componentDidUpdate(prevProps) {
        const currencyFields = [
            { prop: 'currentComp', key: 'currentComp' },
            { prop: 'catchUpAmount', key: 'catchUpAmount' },
            { prop: 'current401', key: 'current401' },
            { prop: 'openingCashBalance', key: 'openingCashBalance' }
        ];

        const updates = {};
        currencyFields.forEach(({ prop, key }) => {
            if (prevProps[prop] !== this.props[prop] && this.state.editingField !== key) {
                updates[key] = formatWithCommas(this.props[prop] || 0);
            }
        });

        if (Object.keys(updates).length > 0) {
            this.setState(prev => ({
                currencyInputs: {
                    ...prev.currencyInputs,
                    ...updates
                }
            }));
        }
    }

    // when the user types in a currency field
    handleCurrencyChange = (fieldKey, parentKey, evt) => {
        const raw = evt.target.value;
        
        // Update currency input state
        this.setState(prev => ({
            currencyInputs: {
                ...prev.currencyInputs,
                [fieldKey]: raw
            }
        }));

        // Try to parse to a number and push up immediately if valid (>= 0)
        const num = formatToNumber(raw);
        const isValid = raw !== '' && !Number.isNaN(num) && num >= 0;

        // Update validity state
        this.setFieldValidity(parentKey, isValid);

        // Only recompute when valid
        this.props.updateState(isValid ? num : raw, parentKey, isValid);
    };

    // Handler for currency fields with slider controls
    handleCurrencySliderChange = (value, fieldKey, parentKey) => {
        // Update the formatted input state with comma formatting
        this.setState(prev => ({
            currencyInputs: {
                ...prev.currencyInputs,
                [fieldKey]: formatWithCommas(value)
            }
        }));
        // Update parent state with the numeric value
        this.props.updateState(value, parentKey, true);
    };

    /**
     * Generic field validator using configuration rules
     * @param {string|number} value - The value to validate
     * @param {string} fieldName - The name of the field being validated
     */
    validateField(value, fieldName) {
        const rule = VALIDATION_RULES[fieldName];
        
        // If no validation rule exists, skip validation
        if (!rule) {
            console.warn(`No validation rule found for field: ${fieldName}`);
            return;
        }

        let isValid = true;
        let processedValue = value;

        // Required field check
        if (rule.required && (value === '' || value === null || value === undefined)) {
            isValid = false;
        } else {
            // Type-specific validation
            if (rule.type === 'number') {
                const num = Number(value);
                
                // Check if it's a valid number
                if (Number.isNaN(num)) {
                    isValid = false;
                } else {
                    processedValue = num;
                    
                    // Check min constraint
                    if (rule.min !== undefined && num < rule.min) {
                        isValid = false;
                    }
                    
                    // Check max constraint (static or from props)
                    let maxValue = rule.max;
                    if (rule.usePropForMax) {
                        maxValue = this.props[rule.usePropForMax];
                    }
                    if (maxValue !== undefined && num > maxValue) {
                        isValid = false;
                    }
                    
                    // Custom validation function
                    if (rule.customValidation && !rule.customValidation(num)) {
                        isValid = false;
                    }
                }
            }
        }

        // Update invalid state flag
        this.setFieldValidity(fieldName, isValid);

        // Propagate to parent component
        this.props.updateState(
            isValid ? processedValue : value,
            fieldName,
            isValid
        );
    }

    render() {
        // Get all metadata in one call
        const metadata = getInputMetadata(
            this.props.inputData,
            this.props.helpTextData,
            this.props.audience
        );

        // Use the prop for the slider max value
        const maxCatchUpContribution = this.props.MaxCatchUpContribution;

        return (
            <>
                <CollapsibleSection
                    title="Personal Information"
                    isOpen={this.state.sections.Personal}
                    onToggle={() => this.toggleSection("Personal")}
                >
                    <UnifiedInputControl
                        id="inputCurrentAge"
                        mode="number"
                        controlType="slider"
                        label={metadata.currentAge.name}
                        value={this.props.currentAge}
                        isInvalid={this.state.invalidFields.has('currentAge')}
                        onChange={(evt) => this.validateField(evt.target.value, "currentAge")}
                        onSliderChange={(value) => this.props.updateState(value, "currentAge", true)}
                        min={18}
                        max={99}
                        visible={metadata.currentAge.visible}
                    />

                    <UnifiedInputControl
                        id="inputRetirementAge"
                        mode="number"
                        controlType="slider"
                        label={metadata.retirementAge.name}
                        value={this.props.retirementAge}
                        isInvalid={this.state.invalidFields.has('retirementAge')}
                        onChange={(evt) => this.validateField(evt.target.value, "retirementAge")}
                        onSliderChange={(value) => this.props.updateState(value, "retirementAge", true)}
                        min={18}
                        max={99}
                        visible={metadata.retirementAge.visible}
                    />

                    <UnifiedInputControl
                        id="inputTotalComp"
                        mode="currency"
                        controlType="arrows"
                        label={metadata.currentComp.name}
                        value={this.state.currencyInputs.currentComp}
                        isInvalid={this.state.invalidFields.has('currentComp')}
                        onFocus={() => this.handleFocus('currentComp')}
                        onBlur={() => this.handleBlur('currentComp')}
                        onChange={(e) => this.handleCurrencyChange('currentComp', 'currentComp', e)}
                        onIncrement={() => this.props.valueUpDown("up", "currentComp", 1000.00)}
                        onDecrement={() => this.props.valueUpDown("down", "currentComp", 1000.00)}
                        incrementAmount={1000}
                        tooltipId="CurrentComp"
                        tooltipText={metadata.currentComp.tooltip}
                        visible={metadata.currentComp.visible}
                    />

                    <UnifiedInputControl
                        id="inputCompRate"
                        mode="percentage"
                        controlType="slider"
                        label={metadata.compRate.name}
                        value={this.props.compRate}
                        isInvalid={this.state.invalidFields.has('compRate')}
                        onChange={(evt) => this.validateField(evt.target.value, "compRate")}
                        onSliderChange={(value) => this.props.updateState(value, "compRate", true)}
                        min={0}
                        max={15}
                        step={0.5}
                        tooltipId="CompRate"
                        tooltipText={metadata.compRate.tooltip}
                        visible={metadata.compRate.visible}
                    />
                </CollapsibleSection>

                <CollapsibleSection
                    title="401(k) Information"
                    isOpen={this.state.sections['401']}
                    onToggle={() => this.toggleSection("401")}
                >
                    <UnifiedInputControl
                        id="inputDeferral"
                        mode="percentage"
                        controlType="slider"
                        label={metadata.deferral.name}
                        value={this.props.deferralPercentage}
                        isInvalid={this.state.invalidFields.has('deferralPercentage')}
                        onChange={(evt) => this.validateField(evt.target.value, "deferralPercentage")}
                        onSliderChange={(value) => this.props.updateState(value, "deferralPercentage", true)}
                        min={0}
                        max={50}
                        step={0.5}
                        tooltipId="Deferral"
                        tooltipText={metadata.deferral.tooltip}
                        visible={metadata.deferral.visible}
                    />

                    <UnifiedInputControl
                        id="inputCatchUp"
                        mode="currency"
                        controlType="slider"
                        label={metadata.catchUp.name}
                        value={this.state.currencyInputs.catchUpAmount}
                        isInvalid={this.state.invalidFields.has('catchUpAmount')}
                        onChange={(evt) => this.validateField(evt.target.value, "catchUpAmount")} 
                        onSliderChange={(value) => this.handleCurrencySliderChange(value, 'catchUpAmount', 'catchUpAmount')}
                        min={0}
                        max={maxCatchUpContribution}
                        step={500}
                        tooltipId="CatchUp"
                        tooltipText={metadata.catchUp.tooltip}
                        visible={metadata.catchUp.visible}
                    />

                    <UnifiedInputControl
                        id="inputCurrent401"
                        mode="currency"
                        controlType="arrows"
                        label={metadata.current401.name}
                        value={this.state.currencyInputs.current401}
                        isInvalid={this.state.invalidFields.has('current401')}
                        onFocus={() => this.handleFocus('current401')}
                        onBlur={() => this.handleBlur('current401')}
                        onChange={(e) => this.handleCurrencyChange('current401', 'current401', e)}
                        onIncrement={() => this.props.valueUpDown("up", "current401", 1000.00)}
                        onDecrement={() => this.props.valueUpDown("down", "current401", 1000.00)}
                        incrementAmount={1000}
                        tooltipId="Current401"
                        tooltipText={metadata.current401.tooltip}
                        visible={metadata.current401.visible}
                    />

                    <UnifiedInputControl
                        id="inputEst401Return"
                        mode="percentage"
                        controlType="slider"
                        label={metadata.est401Return.name}
                        value={this.props.est401Return}
                        isInvalid={this.state.invalidFields.has('est401Return')}
                        onChange={(evt) => this.validateField(evt.target.value, "est401Return")}
                        onSliderChange={(value) => this.props.updateState(value, "est401Return", true)}
                        min={0}
                        max={15}
                        step={0.5}
                        tooltipId="Est401Return"
                        tooltipText={metadata.est401Return.tooltip}
                        visible={metadata.est401Return.visible}
                    />
                </CollapsibleSection>

                <CollapsibleSection
                    title="ESOP Information"
                    isOpen={this.state.sections.ESOP}
                    onToggle={() => this.toggleSection("ESOP")}
                >
                    <UnifiedInputControl
                        id="inputBeginningShares"
                        mode="number"
                        controlType="slider"
                        label={metadata.beginningShares.name}
                        value={this.props.beginningSharesHeld}
                        isInvalid={this.state.invalidFields.has('beginningSharesHeld')}
                        onChange={(evt) => this.validateField(evt.target.value, "beginningSharesHeld")}
                        onSliderChange={(value) => this.props.updateState(value, "beginningSharesHeld", true)}
                        min={0}
                        max={10000}
                        step={1}
                        tooltipId="BeginningShares"
                        tooltipText={metadata.beginningShares.tooltip}
                        visible={metadata.beginningShares.visible}
                    />

                    <UnifiedInputControl
                        id="inputShareIncrease"
                        mode="percentage"
                        controlType="slider"
                        label={metadata.shareIncrease.name}
                        value={this.props.assumedPercentageIncreaseShares}
                        isInvalid={this.state.invalidFields.has('assumedPercentageIncreaseShares')}
                        onChange={(evt) => this.validateField(evt.target.value, "assumedPercentageIncreaseShares")}
                        onSliderChange={(value) => this.props.updateState(value, "assumedPercentageIncreaseShares", true)}
                        min={0}
                        max={15}
                        step={0.5}
                        tooltipId="ShareIncrease"
                        tooltipText={metadata.shareIncrease.tooltip}
                        visible={metadata.shareIncrease.visible}
                    />

                    <UnifiedInputControl
                        id="inputPercentIncrease"
                        mode="percentage"
                        controlType="slider"
                        label={metadata.dividendIncrease.name}
                        value={this.props.assumedPercentageIncreaseDividends}
                        isInvalid={this.state.invalidFields.has('assumedPercentageIncreaseDividends')}
                        onChange={(evt) => this.validateField(evt.target.value, "assumedPercentageIncreaseDividends")}
                        onSliderChange={(value) => this.props.updateState(value, "assumedPercentageIncreaseDividends", true)}
                        min={0}
                        max={15}
                        step={0.5}
                        tooltipId="PercentIncrease"
                        tooltipText={metadata.dividendIncrease.tooltip}
                        visible={metadata.dividendIncrease.visible}
                    />

                    <UnifiedInputControl
                        id="inputCurrentESOP"
                        mode="currency"
                        controlType="arrows"
                        label={metadata.currentESOP.name}
                        value={this.state.currencyInputs.openingCashBalance}
                        isInvalid={this.state.invalidFields.has('openingCashBalance')}
                        onFocus={() => this.handleFocus('openingCashBalance')}
                        onBlur={() => this.handleBlur('openingCashBalance')}
                        onChange={(e) => this.handleCurrencyChange('openingCashBalance', 'openingCashBalance', e)}
                        onIncrement={() => this.props.valueUpDown("up", "openingCashBalance", 1000.00)}
                        onDecrement={() => this.props.valueUpDown("down", "openingCashBalance", 1000.00)}
                        incrementAmount={1000}
                        tooltipId="CurrentESOP"
                        tooltipText={metadata.currentESOP.tooltip}
                        visible={metadata.currentESOP.visible}
                    />

                    <UnifiedInputControl
                        id="inputEstESOPReturn"
                        mode="percentage"
                        controlType="slider"
                        label={metadata.estESOPReturn.name}
                        value={this.props.assumedPercentageIncreaseCash}
                        isInvalid={this.state.invalidFields.has('assumedPercentageIncreaseCash')}
                        onChange={(evt) => this.validateField(evt.target.value, "assumedPercentageIncreaseCash")}
                        onSliderChange={(value) => this.props.updateState(value, "assumedPercentageIncreaseCash", true)}
                        min={0}
                        max={15}
                        step={0.5}
                        tooltipId="EstESOPReturn"
                        tooltipText={metadata.estESOPReturn.tooltip}
                        visible={metadata.estESOPReturn.visible}
                    />
                </CollapsibleSection>
            </>
        );
    }
}
